import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { filter, map, Observable } from 'rxjs';
import { getFirstVideoNotOrthographicOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { settingsStoreToken } from '../../../cross-team-shared/state/setting-state';
import { LutData } from '../../../team-apis/core/lut-data';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { CalibrationService } from '../../misc-services/calibration.service';
import { ProjectionCamerasService } from '../../render/cameras/projection-cameras.service';
import { RenderTriggerService } from '../../render/render-trigger.service';
import { RendererLayer } from '../../render/render-utils';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';

@Component({
  selector: 'h3-projection-renderer',
  templateUrl: './projection-renderer.component.html',
  styleUrls: ['./projection-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RenderTriggerService],
  standalone: false,
})
export class ProjectionRendererComponent
  extends DirectiveSuperclass
  implements OnInit
{
  @Input({ required: true }) size!: Point2D;
  @Input({ required: true }) imageSize!: Point2D;
  @Input({ required: true }) videoOutputName!: string;
  @Input() isInsideZoomPanel = false;
  @Input() lutData?: LutData;

  private readonly settingsStore = inject(settingsStoreToken);
  private readonly store = inject(PointCloudContentStore);
  private readonly projectionCamerasService = inject(ProjectionCamerasService);
  private readonly calibrationService = inject(CalibrationService);

  protected containerSize$!: Observable<Point2D>;
  protected imageSize$!: Observable<Point2D>;

  ngOnInit() {
    this.containerSize$ = this.getInput$('size').pipe(filter(isDefined));
    this.imageSize$ = this.getInput$('imageSize').pipe(filter(isDefined));
    this.projectionCamerasService.addCamera(this.videoOutputName);

    this.updateViewRegion();
    this.setUpSubscriptions();
  }

  getViewBoxString$() {
    let viewBox$ = this.store('viewBoxes')(this.videoOutputName).$;
    if (this.isInsideZoomPanel) {
      viewBox$ = this.store('zoomPanelViewBoxes')(this.videoOutputName).$;
    }
    return viewBox$.pipe(
      // fix me if you change this code
      map((viewBox) => (viewBox ? viewBox.join(' ') : undefined)),
    );
  }

  private setUpSubscriptions() {
    this.subscribeToImageSize();
    this.subscribeToContainerSize();
    this.subscribeToLutData();
  }

  private subscribeToImageSize() {
    this.subscribeTo(this.imageSize$, (size) => {
      this.store('projectionImageSize')(this.videoOutputName).set(size);
    });
  }

  private subscribeToContainerSize() {
    this.subscribeTo(this.containerSize$, (size) => {
      this.store('render')('containerSize')(this.videoOutputName).set(size);
    });
  }

  private subscribeToLutData() {
    // Assumption: only one video output (non-orthographic) per task is
    // supported when using LUTs
    const videoOutput = getFirstVideoNotOrthographicOutput(
      this.store.state().project,
    );
    this.subscribeTo(this.getInput$('lutData'), (lutData) => {
      this.store('render')('lutData').set(lutData);

      assertDefined(videoOutput);
      const projectionType = this.calibrationService.getProjectionType(
        videoOutput.name,
      );

      if (projectionType === 'lut') {
        this.settingsStore('areProjectionsEnabled').set(
          isDefined(lutData?.angleToPixel),
        );
      }
    });
  }

  private updateViewRegion() {
    this.store('viewRegions')('regions')(this.videoOutputName).set({
      layer: RendererLayer.Projection,
      lowerLeftCorner: [0, 0],
      outputName: this.videoOutputName,
      size: this.imageSize,
    });
  }
}
